import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ApiService from "../../../services/categoryManagementApi";

const { reportsAndAnalyticsApi } = ApiService;

// UTC date helpers:
function toUTCStart(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}
function toUTCEnd(date) {
  if (!date) return null;
  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

const DriverReport = () => {
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [driverSearchKey, setDriverSearchKey] = useState("");
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  useEffect(() => {
    fetchDrivers();
    // eslint-disable-next-line
  }, [driverSearchKey]);

  const fetchDrivers = async () => {
    try {
      const res = await reportsAndAnalyticsApi.getAllDrivers({ searchItem: driverSearchKey });
      setDrivers(res?.data?.data || []);
    } catch (error) {
      toast.error("Failed to fetch drivers");
    }
  };

  const fetchReport = async () => {
    if (!selectedDriver) {
      toast.error("Please select a driver.");
      return;
    }
    // Validate date range
    if (fromDate && toDate && toDate < fromDate) {
      toast.error("End date cannot be before start date.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        type: "driverPerformance",
        id: selectedDriver._id,
        startDate: fromDate ? toUTCStart(new Date(fromDate)).toISOString() : undefined,
        endDate: toDate ? toUTCEnd(new Date(toDate)).toISOString() : undefined,
        search: searchText,
      };
      const res = await reportsAndAnalyticsApi.getUserReport(payload);
      setReportData(res?.data?.data?.result || {});
    } catch (error) {
      toast.error("Failed to fetch report");
    } finally {
      setLoading(false);
    }
  };

  // Reset everything (all filters and result)
  const handleReset = () => {
    setSelectedDriver(null);
    setFromDate(null);
    setToDate(null);
    setSearchText("");
    setDriverSearchKey("");
    setReportData(null);
  };

  const deliveries = reportData?.allDeliveries || [];

  const exportData = deliveries.map(order => ({
    "Order Number": order.orderNumber,
    Products: order.products.map(x => `${x.name}-${x.qty}`).join(", "),
    Qty: order.products.reduce((acc, p) => acc + p.qty, 0),
    "Order Total": order.total,
    "Payment Method": order.paymentMethod,
    "Order Status": order.orderStatus?.stage || "-",
    "Order Date": new Date(order.createdAt).toLocaleDateString(),
    "Delivery Address": [
      order.userAddress?.houseNumber,
      order.userAddress?.streetNumber,
      order.userAddress?.village,
      order.userAddress?.commune,
      order.userAddress?.city,
      order.userAddress?.postalCode,
    ].filter(Boolean).join(", "),
  }));

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Driver Performance");
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(blob, `DriverPerformance_${selectedDriver?._id || "report"}.xlsx`);
  };

  // You can optionally add more driver data summary fields like rating, pending deliveries, etc. if present in your API

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={{ xs: 2, sm: 3 }}>
        <Typography variant="h5" gutterBottom>
          Driver Performance Report
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} alignItems="center" mb={2}>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={d => {
                setFromDate(d);
                if (toDate && d && toDate < d) setToDate(null);
              }}
              maxDate={toDate || undefined}
              disableFuture
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <DatePicker
              label="To Date"
              value={toDate}
              onChange={d => setToDate(d)}
              minDate={fromDate || undefined}
              maxDate={new Date()}
              disableFuture
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={drivers}
              value={selectedDriver}
              getOptionLabel={option => option.name || ""}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onInputChange={(_, value) => setDriverSearchKey(value)}
              onChange={(_, value) => setSelectedDriver(value)}
              renderInput={params => <TextField {...params} label="Select Driver" fullWidth />}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="contained"
              onClick={fetchReport}
              disabled={loading}
              sx={{ height: 52 }}
            >
              {loading ? "Loading..." : "Generate Report"}
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              fullWidth
              variant="outlined"
              color="secondary"
              onClick={handleReset}
              sx={{ height: 52 }}
            >
              Reset
            </Button>
          </Grid>
        </Grid>

        {/* Table */}
        {reportData === null ? (
          <Paper
            elevation={2}
            sx={{
              mt: 4,
              width: "100%",
              minHeight: "35vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: "#fafbfc",
              boxSizing: "border-box",
              p: { xs: 2, sm: 4 },
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Select a date range and the driver above, then click <strong>Generate Report</strong> to view stats.
            </Typography>
          </Paper>
        ) : (
          <>
            {/* Summary Card */}
            <Box mb={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#fafbfc",
                      width: "100%",
                      boxSizing: "border-box",
                      p: 2,
                      mb: { xs: 2, md: 0 },
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle1" mb={1} color="primary">
                          <strong>{reportData.name || "-"}</strong>
                        </Typography>
                        <Typography variant="body2">
                          <strong>Phone:</strong> {reportData.countryCode}{reportData.phoneNumber || "-"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Email:</strong> {reportData.email || "-"}
                        </Typography>
                        <Typography variant="body2">
                          <strong>Address:</strong> {reportData.address || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Vehicle
                        </Typography>
                        <Typography variant="body2">
                          <strong>Type:</strong> {reportData.vehicleType || "-"}
                          <br />
                          <strong>Number:</strong> {reportData.vehicleNumber || "-"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                          Delivery Stats
                        </Typography>
                        <Typography variant="body2">
                          <strong>Total Deliveries:</strong> {reportData.totalDeliveries ?? "-"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                    width="100%"
                    sx={{ mb: 1 }}
                  >
                    <Button variant="outlined" onClick={exportToExcel}>
                      Export Excel
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            {/* Table */}
            <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order #</TableCell>
                    <TableCell>Products</TableCell>
                    <TableCell>Qty</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Payment</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Delivery Address</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deliveries.length > 0 ? (
                    deliveries.map((order, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{order.orderNumber}</TableCell>
                        <TableCell>{order.products.map(x => `${x.name}-${x.qty}`).join(", ")}</TableCell>
                        <TableCell>{order.products.reduce((acc, p) => acc + p.qty, 0)}</TableCell>
                        <TableCell>{order.total}</TableCell>
                        <TableCell>{order.paymentMethod}</TableCell>
                        <TableCell>{order.orderStatus?.stage || "-"}</TableCell>
                        <TableCell>{new Date(order.createdAt).toLocaleDateString()}</TableCell>
                        <TableCell>
                          {[
                            order.userAddress?.houseNumber,
                            order.userAddress?.streetNumber,
                            order.userAddress?.village,
                            order.userAddress?.commune,
                            order.userAddress?.city,
                            order.userAddress?.postalCode,
                          ].filter(Boolean).join(", ")}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        No Data Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

      </Box>
    </LocalizationProvider>
  );
};

export default DriverReport;
